import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnsType } from 'antd/es/table';
import { Modal, ModalSize, Pagination } from '@/components';
import { Table, Link } from '@/ui';
import { useAppDispatch, useAppState } from '@/store';
import { formatPercentValue } from '@/utils';
import { ROUTES } from '@/models/router.model';
import { ICancelledPartnerItem } from '@/models/cancelledOrders.model';
import { cancelledOrdersSelector, getCancelledPartnersList } from '../../feature';
import { sortCancelledPartnersByShare } from '../../helpers';
import DynamicsCell from '../DynamicsCell/DynamicsCell';

interface IAllPartnersModalProps {
  open: boolean;
  onClose: () => void;
}

const AllPartnersModal = ({ open, onClose }: IAllPartnersModalProps) => {
  const { t, i18n } = useTranslation(['dashboard']);
  const dispatch = useAppDispatch();
  const { partnersList, partnersMeta, isListLoading, query } = useAppState(cancelledOrdersSelector);
  const [page, setPage] = useState(1);
  const ppSuffix = t('cancelled_orders_pp_suffix');

  useEffect(() => {
    if (!open) return;
    dispatch(
      getCancelledPartnersList({
        date_from: query.date_from,
        date_to: query.date_to,
        page,
        per_page: 20,
      }),
    );
  }, [open, page, query.date_from, query.date_to, dispatch]);

  const dataSource = useMemo(() => sortCancelledPartnersByShare(partnersList), [partnersList]);

  const columns: ColumnsType<ICancelledPartnerItem> = [
    {
      title: t('cancelled_orders_partner'),
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record) => <Link path={`${ROUTES.PARTNER_DETAILS_INDEX}/${record.id}`}>{name}</Link>,
    },
    {
      title: t('cancelled_orders_share'),
      dataIndex: 'cancelled_share_percent',
      key: 'cancelled_share_percent',
      render: (value: number) => formatPercentValue(value, 1, i18n.language),
    },
    {
      title: t('cancelled_orders_dynamics'),
      dataIndex: 'change_percent',
      key: 'change_percent',
      render: (value: number | null) => <DynamicsCell value={value} suffix={ppSuffix} />,
    },
    {
      title: t('cancelled_orders_count'),
      dataIndex: 'cancelled_count',
      key: 'cancelled_count',
      render: (value?: number) => value ?? '—',
    },
  ];

  return (
    <Modal visible={open} onCancel={onClose} size={ModalSize.HUGE} title={t('cancelled_orders_all_partners_title')}>
      <Table rowKey="id" columns={columns} dataSource={dataSource} loading={isListLoading} />
      {partnersMeta ? (
        <Pagination
          current={partnersMeta.current_page}
          total={partnersMeta.total}
          pageSize={partnersMeta.per_page}
          onChange={setPage}
        />
      ) : null}
    </Modal>
  );
};

export default AllPartnersModal;
